import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, cn, Label } from '@c2fo/liquidity';
import FilterDrawer from '@/enterprise/components/FilterDrawer';
import RangePicker from '@/enterprise/components/RangePicker';
import { dateRanges, getRangeKey, getRangeLabel, type DateRange, type RangeKey } from './utils';

export interface HistoryFilterProps {
  customers?: {
    label: string;
    value: string[];
  }[];
  filterOpen: boolean;
  onCustomersChange?: (makerOrganizationUuids: string[]) => void;
  onDateRangeChange: (bag: { range: DateRange; rangeKey: RangeKey }) => void;
  setFilterOpen: (open: boolean) => void;
  endDate: string;
  startDate: string;
  rangeKey: string;
  makerOrganizationUuids?: string[];
}

const HistoryFilter = ({
  customers,
  filterOpen,
  onCustomersChange,
  onDateRangeChange,
  setFilterOpen,
  startDate,
  endDate,
  rangeKey: rangeKeyProp,
  makerOrganizationUuids,
}: HistoryFilterProps) => {
  const { t } = useTranslation();
  const [customPickerOpen, setCustomPickerOpen] = useState(false);

  const isValidRangeKey = (key: string | undefined, start: string, end: string): key is RangeKey => {
    if (!key || key === 'custom') {
      return key === 'custom';
    }

    if (!(key in dateRanges)) {
      return false;
    }

    const range = dateRanges[key as keyof typeof dateRanges];
    return range.startDate === start && range.endDate === end;
  };

  // local state to track which radio button is selected
  const [selectedRangeKey, setSelectedRangeKey] = useState<RangeKey>(() =>
    isValidRangeKey(rangeKeyProp, startDate, endDate) ? (rangeKeyProp as RangeKey) : getRangeKey(startDate, endDate)
  );

  const prevPropsRef = useRef({ startDate, endDate, rangeKeyProp });

  // sync local state when URL params change externally
  useEffect(() => {
    const prev = prevPropsRef.current;

    if (prev.startDate !== startDate || prev.endDate !== endDate || prev.rangeKeyProp !== rangeKeyProp) {
      setSelectedRangeKey(
        isValidRangeKey(rangeKeyProp, startDate, endDate) ? (rangeKeyProp as RangeKey) : getRangeKey(startDate, endDate)
      );
      prevPropsRef.current = { startDate, endDate, rangeKeyProp };
    }
  }, [startDate, endDate, rangeKeyProp]);

  return (
    <FilterDrawer filterOpen={filterOpen} setFilterOpen={setFilterOpen}>
      <div className="space-y-4 p-4">
        <div className="w-full space-y-1">
          <Label>{t('core.depositDate')}</Label>
          <div className="rounded border border-stroke bg-white p-4">
            <ul className="space-y-2">
              {Object.entries(dateRanges).map(([key, range]) => (
                <li key={key}>
                  <label htmlFor={key} className="flex cursor-pointer items-center gap-2">
                    <input
                      id={key}
                      className="h-5 w-5 accent-secondary-500 focus:ring-secondary-600"
                      checked={selectedRangeKey === key}
                      onChange={() => {
                        setSelectedRangeKey(key as RangeKey);
                        onDateRangeChange({ range, rangeKey: key as RangeKey });
                      }}
                      type="radio"
                    />
                    {t(...getRangeLabel(key))}
                  </label>
                </li>
              ))}
              <li>
                <RangePicker
                  className={cn({ 'font-bold': selectedRangeKey === 'custom' })}
                  onChange={(range) => {
                    setSelectedRangeKey('custom');
                    onDateRangeChange({
                      range: range ?? dateRanges.last7Days,
                      rangeKey: 'custom',
                    });
                  }}
                  onOpenChange={setCustomPickerOpen}
                  open={customPickerOpen}
                  value={{ startDate: startDate!, endDate: endDate! }}
                />
              </li>
            </ul>
          </div>
        </div>
        {customers && customers.length > 0 ? (
          <div className="w-full space-y-1">
            <Label>{t('core.customers')}</Label>
            <div className="max-h-80 space-y-2 overflow-y-auto rounded border border-stroke bg-white p-4">
              {customers.map(({ label, value }) => {
                const isChecked = value.some((uuid) => (makerOrganizationUuids ?? []).includes(uuid));

                return (
                  <Checkbox
                    key={label}
                    checked={isChecked}
                    color="secondary"
                    id={`customer-${label.replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase()}`}
                    label={label}
                    onChange={() => onCustomersChange?.(value)}
                    value={label}
                  />
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
      <div className="flex justify-end px-4 pb-4">
        <Button onClick={() => setFilterOpen(false)} variant="secondary">
          {t('core.close')}
        </Button>
      </div>
    </FilterDrawer>
  );
};

export default HistoryFilter;
